import { Button, Chip, Grid, Typography } from '@material-ui/core';
import { unstable_Box as Box } from '@material-ui/core/Box';
import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import React from 'react';
import { MdOutlineAccountCircle, MdOutlineBook, MdOutlineTimer } from 'react-icons/md';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { Academy } from '@aps-management/primapp-common';

/* */
import { Screen } from '_components/core';
import { CalendarDay } from '_components/elements';
import masterClient from '_utils/masterClient';

/* */
import Header from '../_Header';

const getminMaxColor = (min, max) => {
  switch (true) {
    case min >= max:
      return 'rgba(217, 89, 89, 1)';
    case min >= max / 2:
      return 'rgba(239, 116, 39, 1)';
    default:
      return 'rgba(61, 195, 77, 1)';
  }
};
/* */
const styles = (theme) => ({
  players: {
    marginLeft: theme.spacing.unit * 1,
    height: 25,
    fontWeight: 'bold',
    borderRadius: '5px',
    backgroundColor: 'rgba(61, 195, 77, 1)',
    cursor: 'pointer',
  },
  slots: {
    marginLeft: theme.spacing.unit * 1,
    height: 25,
    fontWeight: 'bold',
    borderRadius: '5px',
    backgroundColor: 'rgba(132, 189, 227, 1)',
    cursor: 'pointer',
  },
});
const Enter = (props) => {
  const history = useHistory();
  const { golf, account, classes } = props;
  const { id } = useParams();

  // Configuration des options d'affichage Academy
  const { options: { academy = {} } = {} } = golf || {};
  const { booking: academyBooking = {} } = academy;
  const { showOthersOnLesson = true } = academyBooking;

  const { data: lesson, error: lessonErrors } = useQuery(
    ['academy-lesson', id],
    () =>
      Academy.api.getLesson(masterClient, {
        id,
      }),
    {
      enabled: !!id,
      retry: 0,
      cacheTime: 0,
      refetchOnWindowFocus: true,
      refetchInterval: 5000,
      refetchIntervalInBackground: true,
      initialData: () => null,
    },
  );

  const { data: entryList, error: registrationErrors } = useQuery(
    ['academy-lesson-registrations', lesson?.id],
    () =>
      Academy.api.getLessonRegistrations(masterClient, {
        lessonId: lesson?.id,
      }),
    {
      enabled: !!lesson?.id,
      retry: 0,
      cacheTime: 0,
      refetchOnWindowFocus: true,
      refetchInterval: 5000,
      refetchIntervalInBackground: true,
      initialData: () => null,
    },
  );
  // `getLessonRegistrations` ne filtre pas sur le statut : une inscription annulée
  // revient dans la liste. Sans l'exclure ici, un golfeur qui annule son cours voit
  // toujours « Vous êtes déjà inscrit » et ne peut plus jamais s'y réinscrire, alors
  // que le compteur de places, lui, l'a bien libérée (cf. renderSlotsList).
  const entry = !!entryList?.find((res) => res.customer.id === account.id && res.status !== 'CANCELED');

  // Vérifier le quota maxInDay pour l'utilisateur connecté
  const { data: quotaData, isLoading: isLoadingQuota } = useQuery(
    ['academy-lesson-quotas', account?.id, lesson?.type],
    () =>
      Academy.api.getLessonRegistrationsQuota(masterClient, {
        customerId: account?.id,
        lessonType: lesson?.type,
      }),
    {
      enabled: !!account?.id && !!lesson?.type,
      retry: 0,
      cacheTime: 0,
    },
  );

  // Récupérer maxInDay depuis les options du golf
  const maxInDay = React.useMemo(() => {
    const restrictions =
      lesson?.type === 'INDIVIDUAL'
        ? golf?.options?.academy?.onlineRestrictions?.individual
        : golf?.options?.academy?.onlineRestrictions?.group;
    return restrictions?.maxInDay; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [golf, lesson?.type]); // eslint-disable-next-line react-hooks/exhaustive-deps

  // Calculer si maxInDay est dépassé pour le jour du cours
  const isMaxInDayExceeded = React.useMemo(() => {
    // Si maxInDay n'est pas défini ou vaut -1, pas de limite
    if (!maxInDay || maxInDay === -1) {
      return false;
    }

    // Filtrer les réservations pour ne garder que celles du jour du cours
    const lessonDay = moment(lesson?.startsAt);
    const todayStart = lessonDay.clone().startOf('day');
    const todayEnd = lessonDay.clone().endOf('day');

    const registrationsToday = (quotaData?.activeRegistrations || []).filter((registration) => {
      const lessonStart = moment(registration.lesson.startsAt);
      return lessonStart.isBetween(todayStart, todayEnd, null, '[]');
    });

    return registrationsToday.length >= maxInDay; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotaData, maxInDay, lesson?.startsAt]); // eslint-disable-next-line react-hooks/exhaustive-deps

  const goToMyAcademy = () => {
    history.push(`/myacademy/${lesson?.id}`);
  };
  /* */
  const renderEntry = () => {
    if (!entry) return null;
    return (
      <React.Fragment>
        <Typography variant='body1'>{'Vous êtes déjà inscrit à ce cours.'}</Typography>
        <br />
        <Button color='secondary' variant='outlined' onClick={goToMyAcademy}>
          {'Voir mon inscription'}
        </Button>
      </React.Fragment>
    );
  };

  const renderSlotsList = () => {
    if (!lesson) return null;
    const { slotCount } = lesson;
    return (
      <Box mt={3} borderColor='#ddd'>
        <Box display='flex' flexDirection='row'>
          <MdOutlineBook size={24} />
          &nbsp;
          <Typography gutterBottom variant='body1'>
            {'Liste des créneaux :'}
          </Typography>
          <Chip className={classes.slots} color='default' size='small' label={slotCount} />
        </Box>
        <Grid container spacing={8} style={{ paddingBottom: 12 }}>
          {lesson.coachSlots
            .filter((s) => s.status !== 'CANCELED')
            .map((s, key) => (
              <Grid item key={key}>
                <CalendarDay key={key} displayTime datetime={moment(s?.startsAt)} endTime={moment(s?.endsAt)} />
              </Grid>
            ))}
        </Grid>
      </Box>
    );
  };

  /* */
  const renderEntryList = () => {
    if (!lesson) return null;
    if (!showOthersOnLesson) return null;
    const { attendeeCount, maxGroupSize } = lesson;
    if (!entryList?.length) return null;

    const columns = 3;
    // La découpe en colonnes se calculait sur la liste ENTIÈRE alors que les deux
    // grilles ci-dessous n'affichent que les CONFIRMED et les WAITLISTED : dès qu'un
    // cours comptait des inscriptions annulées, les tranches débordaient et les
    // dernières colonnes ressortaient vides.
    const confirmes = entryList.filter((e) => e.status === 'CONFIRMED');
    const enAttente = entryList.filter((e) => e.status === 'WAITLISTED');
    const perColumn = Math.ceil(confirmes.length / columns);
    const perColumnAttente = Math.ceil(enAttente.length / columns);

    return (
      <Box mt={3} borderColor='#ddd'>
        <Box display='flex' flexDirection='row'>
          <MdOutlineAccountCircle size={24} />
          &nbsp;
          <Typography gutterBottom variant='body1'>
            {'Liste des inscrits :'}
          </Typography>
          <Chip
            className={classes.players}
            style={{
              backgroundColor: getminMaxColor(attendeeCount + enAttente.length, maxGroupSize),
            }}
            color='default'
            size='small'
            label={`${attendeeCount + enAttente.length} / ${maxGroupSize}`}
          />
        </Box>
        <Grid container>
          {[...Array(columns)].map((und, c) => (
            <Grid item xs={12} sm={4} key={c}>
              {confirmes
                .slice(c * perColumn, c * perColumn + perColumn)
                .map((e, key) => (
                  <Typography
                    key={key}
                    variant='caption'
                  >{`${e.customer.lastName} ${e.customer.firstName}`}</Typography>
                ))}
            </Grid>
          ))}
        </Grid>
        {enAttente.length ? (
          <>
            <Box display='flex' flexDirection='row'>
              <MdOutlineTimer size={24} />
              &nbsp;
              <Typography gutterBottom variant='body1'>
                {"En liste d'attente :"}
              </Typography>
            </Box>
            <Grid container>
              {[...Array(columns)].map((und, c) => (
                <Grid item xs={12} sm={4} key={c}>
                  {enAttente
                    .slice(c * perColumnAttente, c * perColumnAttente + perColumnAttente)
                    .map((e, key) => (
                      <Typography
                        key={key}
                        variant='caption'
                      >{`${e.customer.lastName} ${e.customer.firstName}`}</Typography>
                    ))}
                </Grid>
              ))}
            </Grid>
          </>
        ) : null}
      </Box>
    );
  };

  const goToNext = () => history.push(`/academy/${id}/entrants`);
  /* */
  const renderEnterButton = () => {
    const { maxGroupSize: limit, attendeeCount: counter } = lesson;

    // Inscription ouverte + joueur non inscrit
    const isOpened = !entry && Academy.functions.isOpened({ ...lesson, openOnline: true }, golf?.options);
    if (!isOpened) return null;

    // Vérifier si le joueur a dépassé son quota ou sa limite journalière
    if (quotaData?.isExceeded || isMaxInDayExceeded) {
      return (
        <Button disabled fullWidth size='large' onClick={null} color='primary' variant='contained'>
          {'Quota de réservations atteint'}
        </Button>
      );
    }

    // Limite atteinte
    if (limit && entryList && counter + entryList.filter((e) => e.status === 'WAITLISTED').length >= limit) {
      return (
        <Button disabled fullWidth size='large' onClick={null} color='primary' variant='contained'>
          {'Complet'}
        </Button>
      );
    }
    return !entryList ? null : (
      <Box borderBottom={1}>
        <Button
          fullWidth
          size='large'
          color='secondary'
          variant='contained'
          onClick={goToNext}
          disabled={isLoadingQuota}
        >
          {isLoadingQuota ? 'Vérification...' : "S'inscrire"}
        </Button>
      </Box>
    );
  };
  const error = lessonErrors || registrationErrors;
  return (
    <Screen error={error} loading={!entryList} title="Académie - S'inscrire">
      {lesson && (
        <React.Fragment>
          <Header lesson={lesson} golf={golf} />
          {renderEntry()}
          {renderEnterButton()}
          {renderSlotsList()}
          {renderEntryList()}
        </React.Fragment>
      )}
    </Screen>
  );
};

const mapStateToProps = ({ app }) => ({
  golf: app.golf,
  account: app.account,
});

/* */
const StyledComponent = withStyles(styles)(Enter);

export default connect(mapStateToProps)(withWidth()(StyledComponent));
